import { createContext, type Dispatch, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import type { DashboardSpec } from "../ChartSpec";
import { type DashboardSpecAction, specReducer } from "./spec-reducer";

export const SpecContext = createContext<{ spec: DashboardSpec; dispatch: Dispatch<DashboardSpecAction> } | null>(null);

export function SpecProvider({ initialSpec, children }: { initialSpec: DashboardSpec; children: React.ReactNode }) {
    const [spec, dispatch] = useReducer(specReducer, initialSpec);
    const initialSpecRef = useRef(initialSpec);

    // useReducer only reads initialSpec on mount; parent memoizes so reference change means "replace document".
    useEffect(() => {
        if (initialSpecRef.current === initialSpec) return;
        initialSpecRef.current = initialSpec;
        dispatch({ type: "update", payload: initialSpec });
    }, [initialSpec]);

    const value = useMemo(() => ({ spec, dispatch }), [spec]);

    return <SpecContext.Provider value={value}>{children}</SpecContext.Provider>;
}

export function useSpec() {
    const context = useContext(SpecContext);
    if (!context) {
        throw new Error("useSpec must be used within a SpecProvider");
    }
    return context;
}
